/**
 * Settings, Sign-in: how people sign in (GitHub, Google, Microsoft), who may use Google and
 * Microsoft, who joins on their own, and the sign-ins linked to your own account.
 *
 * Everyone sees the provider status and their own sign-ins. Admins also see redirect URIs,
 * client ids, problems and setup steps. Only owners change who may sign in and join rules
 * (the hub enforces all of it; this view only mirrors it).
 */
import { Ban, Building2, CircleAlert, Globe, Link2, Lock, LogOut, Plus, ShieldCheck, Sparkles, Trash2, Unlink, UserPlus } from '@orgus/ui/icons';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { MICROSOFT_CONSUMERS_TENANT, ROLE_RANK, SIGN_IN_PROVIDER_LABEL, roleAtLeast } from '@orgus/protocol';
import type {
  AuthState,
  JoinRule,
  LinkedIdentity,
  Me,
  MyIdentities,
  OidcProviderId,
  PolicyPreview,
  ProviderStatus,
  SignInPolicyRequest,
  SignInSettings,
} from '@orgus/protocol';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Panel,
  RelativeTime,
  Segmented,
  Select,
  SkeletonRows,
  StatusPill,
  Tooltip,
  cn,
  toast,
} from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { useExt } from '../../lib/store.ts';
import { authClient, needsFreshSignIn } from './client.ts';
import { CopyField } from './CopyField.tsx';
import { ACCOUNT_TYPE_LABEL, MICROSOFT_AUDIENCE_LABEL, ROLE_LABEL, ROLE_VALUE_CLASS, addedLabel, providerStatusOf, shortTenant } from './labels.ts';
import { ProviderMark } from './ProviderMark.tsx';
import { useMe } from './useMe.ts';
import { useResource } from './useResource.ts';

// ------------------------------------------------------------------ input checks (the hub checks again)

const DOMAIN = /^(?=.{3,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const PUBLIC_MAIL = new Set(['gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'yahoo.com', 'icloud.com', 'proton.me', 'protonmail.com', 'aol.com']);

/** Null when `v` is a Workspace domain the hub would accept, else why not. */
export function checkWorkspaceDomain(v: string): string | null {
  if (v === 'gmail.com' || v === 'googlemail.com') return 'Personal Gmail accounts have no Workspace domain to match. Leave the list empty to let any Google account try.';
  if (!DOMAIN.test(v)) return 'Use a domain such as acme.com, without @ or https://.';
  return null;
}

/** Null when `v` is a tenant id; the personal-account tenant only where `allowConsumers`. */
export function checkTenant(v: string, allowConsumers: boolean): string | null {
  if (!GUID.test(v)) return 'Use the tenant id from Microsoft Entra, Overview: a GUID such as 72f988bf-86f1-41af-91ab-2d7cd011db47.';
  if (!allowConsumers && v === MICROSOFT_CONSUMERS_TENANT) return 'The personal-account tenant cannot be used here: anyone can make a personal Microsoft account.';
  return null;
}

/** Null when `v` can be a trusted email domain for a work tenant. */
export function checkTrustedDomain(v: string): string | null {
  if (PUBLIC_MAIL.has(v)) return `${v} is a public mail domain; anyone can have an address there.`;
  if (!DOMAIN.test(v)) return 'Use a domain such as contoso.com.';
  return null;
}

const norm = (v: string) => v.trim().toLowerCase().replace(/^@/, '');

// ------------------------------------------------------------------ small pieces

function Steps({ children }: { children: ReactNode }) {
  return <ol className="flex flex-col gap-4">{children}</ol>;
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-hover text-sm font-medium text-fg">{n}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-2 text-base text-fg">{children}</div>
    </li>
  );
}

function ExtLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a className="text-accent-text underline-offset-2 hover:underline" href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

function SourceTag({ source }: { source: 'config' | 'settings' }) {
  if (source === 'settings') return null;
  return (
    <Tooltip content="Set in auth.json on the hub. Edit that file to change it.">
      <span className="inline-flex items-center gap-1 text-caption text-fg-subtle" tabIndex={0}>
        <Lock className="size-3" aria-hidden /> auth.json
      </span>
    </Tooltip>
  );
}

/** A removable value: domain or tenant id, with where it comes from. */
function ValueChip({
  value,
  detail,
  title,
  source,
  onRemove,
  canEdit,
}: {
  value: string;
  /** A quiet second fact inside the chip, such as the tenant a domain is trusted for. */
  detail?: string;
  title?: string;
  source: 'config' | 'settings';
  onRemove?: () => void;
  canEdit: boolean;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-control border border-border bg-bg py-1 pl-2 pr-1 text-sm text-fg">
      <span className="min-w-0 truncate font-mono" title={title ?? value}>
        {value}
      </span>
      {detail && <span className="shrink-0 text-caption text-fg-subtle">{detail}</span>}
      <SourceTag source={source} />
      {canEdit && source === 'settings' && onRemove && <IconButton label={`Remove ${value}`} icon={<Trash2 />} size="sm" variant="ghost" onClick={onRemove} />}
    </span>
  );
}

function OwnerOnlyNote() {
  return (
    <p className="flex items-center gap-2 text-sm text-fg-subtle">
      <Lock className="size-3.5" aria-hidden /> Only an owner can change this.
    </p>
  );
}

/** Add one value; checks it before asking the hub. */
function AddValueForm({ label, placeholder, check, onAdd, button = 'Add' }: { label: string; placeholder: string; check: (v: string) => string | null; onAdd: (v: string) => Promise<void>; button?: string }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = norm(value);
    const problem = v ? check(v) : 'Type a value first.';
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      await onAdd(v);
      setValue('');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-start gap-2" noValidate>
      <Input
        size="sm"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={error ?? undefined}
        wrapperClassName="min-w-60 flex-1"
        autoComplete="off"
        spellCheck={false}
      />
      <Button type="submit" size="sm" variant="secondary" icon={<Plus />} loading={busy}>
        {button}
      </Button>
    </form>
  );
}

// ------------------------------------------------------------------ policy changes with a dry run

interface PendingPolicy {
  body: SignInPolicyRequest;
  preview: PolicyPreview;
  what: string;
}

/** Saves a policy change: asks the hub what it would do first, and confirms when it signs anyone out. */
function usePolicySave(onChanged: () => void) {
  const [pending, setPending] = useState<PendingPolicy | null>(null);
  const apply = async (body: SignInPolicyRequest, what: string) => {
    await authClient.setSignInPolicy(body);
    toast.success(what);
    onChanged();
  };
  const save = async (body: SignInPolicyRequest, what: string) => {
    const preview = await authClient.setSignInPolicy(body, { dryRun: true });
    if (preview.sessionsEnded > 0 || preview.identitiesNoLongerAllowed > 0) setPending({ body, preview, what });
    else await apply(body, what);
  };
  const dialog = (
    <ConfirmDialog
      open={!!pending}
      onOpenChange={(v) => !v && setPending(null)}
      title="Sign people out with this change?"
      description={
        pending
          ? `This signs out ${pending.preview.sessionsEnded} ${pending.preview.sessionsEnded === 1 ? 'person' : 'people'} now. ${pending.preview.identitiesNoLongerAllowed} linked ${pending.preview.identitiesNoLongerAllowed === 1 ? 'account' : 'accounts'} can no longer sign in. They cannot sign in again with these accounts.`
          : ''
      }
      confirmLabel="Save and sign out"
      onConfirm={async () => {
        if (!pending) return;
        await apply(pending.body, pending.what);
        setPending(null);
      }}
    />
  );
  return { save, dialog };
}

const settingsValues = (xs: Array<{ value: string; source: 'config' | 'settings' }>) => xs.filter((x) => x.source === 'settings').map((x) => x.value);

function trustedFromSettings(p: ProviderStatus): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const t of p.microsoft?.trustedEmailDomains ?? []) if (t.source === 'settings') out[t.tenantId] = [...(out[t.tenantId] ?? []), ...t.domains];
  return out;
}

// ------------------------------------------------------------------ your sign-ins

const NEW_FOR_MS = 7 * 24 * 60 * 60_000;

/** Linked in the last week by someone or something other than the member signing up. */
export function isNewSignIn(i: LinkedIdentity, now = Date.now()): boolean {
  return i.addedVia !== 'created' && i.addedVia !== 'join' && now - i.linkedAt < NEW_FOR_MS;
}

/** The sentence under an invite-added sign-in that the member has not confirmed. */
export function confirmNote(i: LinkedIdentity): string | null {
  if (!i.awaitingConfirm) return null;
  return i.suspended
    ? `It stopped working because ${i.addedBy} can no longer manage your account. Keep it if it is yours, or unlink it.`
    : `It works only while ${i.addedBy} can manage your account. Keep it if it is yours; unlink it if you do not know it.`;
}

function IdentityRow({
  i,
  lastWayIn,
  onUnlink,
  onConfirm,
  confirming,
}: {
  i: LinkedIdentity;
  lastWayIn: boolean;
  onUnlink: (i: LinkedIdentity) => void;
  onConfirm: (i: LinkedIdentity) => void;
  confirming: boolean;
}) {
  const blocked = i.anchor ? 'This account admitted you to the hub, so it stays. Ask an admin if it really has to go.' : lastWayIn ? 'It is your only way to sign in. Link another account first.' : null;
  const note = confirmNote(i);
  // One chip per row, the one that matters most; the account type joins the meta line.
  const chip = i.awaitingConfirm ? (
    i.suspended ? (
      <Badge tone="failed" icon={<Ban />}>
        Stopped working
      </Badge>
    ) : (
      <Badge tone="waiting" icon={<CircleAlert />}>
        Not confirmed
      </Badge>
    )
  ) : !i.emailVerified ? (
    <Badge tone="waiting" icon={<CircleAlert />}>
      Unverified email
    </Badge>
  ) : i.anchor ? (
    <Badge tone="accent" icon={<ShieldCheck />}>
      Admitted you
    </Badge>
  ) : isNewSignIn(i) ? (
    // The warm New marker (colour.md, the warm role): the one place it shows on this page.
    <Badge className="prism-new" icon={<Sparkles />}>
      New
    </Badge>
  ) : null;
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-control border border-border-subtle bg-bg">
          <ProviderMark id={i.provider} className="size-4 text-fg" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="min-w-0 break-all font-medium text-fg">{i.email ?? `${SIGN_IN_PROVIDER_LABEL[i.provider]} account`}</span>
            {chip && (
              <Tooltip content={note} enabled={!!note}>
                <span tabIndex={note ? 0 : undefined} className="inline-flex shrink-0">
                  {chip}
                </span>
              </Tooltip>
            )}
          </span>
          <span className="text-caption text-fg-subtle">
            {[SIGN_IN_PROVIDER_LABEL[i.provider], i.accountType ? ACCOUNT_TYPE_LABEL[i.accountType] : null].filter(Boolean).join(', ')}. {addedLabel(i, true)}, <RelativeTime ts={i.linkedAt} />. Last used{' '}
            <RelativeTime ts={i.lastUsedAt} fallback="never" />
          </span>
          {note && <span className="sr-only">{note}</span>}
        </div>
      </div>
      {/* On a phone the actions sit under the text, so the text keeps the full width. */}
      <div className="flex shrink-0 items-center gap-2">
        {i.awaitingConfirm && (
          <Button size="sm" variant="secondary" icon={<ShieldCheck />} loading={confirming} disabled={confirming} onClick={() => onConfirm(i)}>
            Keep this sign-in
          </Button>
        )}
        <Tooltip content={blocked} enabled={!!blocked}>
          <span tabIndex={blocked ? 0 : undefined}>
            <Button size="sm" variant="ghost" icon={<Unlink />} disabled={!!blocked} onClick={() => onUnlink(i)}>
              Unlink
            </Button>
          </span>
        </Tooltip>
      </div>
    </li>
  );
}

export function YourSignIns({ me, mine, error, onRetry, onChanged }: { me: Me; mine: MyIdentities | null; error: unknown; onRetry: () => void; onChanged: () => void }) {
  const [unlinking, setUnlinking] = useState<LinkedIdentity | null>(null);
  const [linking, setLinking] = useState<OidcProviderId | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [endingAll, setEndingAll] = useState(false);
  const [fresh, setFresh] = useState(false);
  const member = me.member;
  const ownerLink = me.via === 'owner-link';
  const viaToken = me.via === 'token';
  const githubMember = member?.provider === 'github';
  const ids = mine?.identities ?? [];
  const lastWayIn = !githubMember && ids.length <= 1;

  const link = async (p: OidcProviderId) => {
    setLinking(p);
    try {
      await authClient.linkIdentity(p);
      // Only demo data comes back here; a real hub leaves for the provider.
      toast.success(`${SIGN_IN_PROVIDER_LABEL[p]} is linked`, 'Demo data: a real hub opens the provider first.');
      onChanged();
    } catch (e) {
      toast.error(e);
      if (needsFreshSignIn(e)) setFresh(true);
    } finally {
      setLinking(null);
    }
  };

  const confirm = async (i: LinkedIdentity) => {
    setConfirming(i.id);
    try {
      await authClient.confirmIdentity(i.id);
      toast.success('The sign-in is confirmed', `${i.email ?? SIGN_IN_PROVIDER_LABEL[i.provider]} no longer depends on ${i.addedBy}.`);
      onChanged();
    } catch (e) {
      toast.error(e);
      if (needsFreshSignIn(e)) setFresh(true);
    } finally {
      setConfirming(null);
    }
  };
  const unconfirmed = ids.filter((i) => i.awaitingConfirm);

  return (
    <Panel title="Your sign-ins" icon={<Link2 />} sub={mine ? `${ids.length + (githubMember ? 1 : 0)}` : undefined} flush>
      {error != null && !mine ? (
        <div className="p-4">
          <ErrorState variant="inline" error={error} onRetry={onRetry} />
        </div>
      ) : !mine ? (
        <div className="p-4" role="status" aria-label="Loading your sign-ins">
          <SkeletonRows rows={2} />
        </div>
      ) : (
        <>
          {ownerLink && (
            <p className="border-b border-border-subtle px-4 py-3 text-sm text-fg-muted">
              Link a Google or Microsoft account to sign in without the owner link.
            </p>
          )}
          {viaToken && <p className="border-b border-border-subtle px-4 py-3 text-sm text-fg-muted">Linking needs a browser sign-in, not an API token.</p>}
          {unconfirmed.length > 0 && (
            <div className="flex items-start gap-2 border-b border-status-attention-line bg-status-attention-soft px-4 py-3 text-sm text-fg" role="status">
              <CircleAlert className="mt-1 size-4 shrink-0 text-status-attention" aria-hidden />
              <span>
                {unconfirmed.length === 1 ? 'An admin added a sign-in to your account.' : `Admins added ${unconfirmed.length} sign-ins to your account.`} Keep yours, unlink any you do not know, then sign out everywhere.
              </span>
            </div>
          )}
          {fresh && (
            <p className="border-b border-border-subtle px-4 py-3 text-sm text-fg-muted">
              Sign out and in again first: confirming and linking need a sign-in from the last 10 minutes.
            </p>
          )}
          <ul className="divide-y divide-border-subtle">
            {githubMember && member && (
              <li className="flex items-center gap-3 px-4 py-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-control border border-border-subtle bg-bg">
                  <ProviderMark id="github" className="size-4 text-fg" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate font-medium text-fg">@{member.login}</span>
                  <span className="text-sm text-fg-subtle">GitHub, through the allow list.</span>
                </div>
              </li>
            )}
            {ids.map((i) => (
              <IdentityRow key={i.id} i={i} lastWayIn={lastWayIn} onUnlink={setUnlinking} onConfirm={(x) => void confirm(x)} confirming={confirming === i.id} />
            ))}
          </ul>
          {!ids.length && !githubMember && !ownerLink && !viaToken && (
            <EmptyState icon={<Link2 />} title="No Google or Microsoft account is linked" description="Link one below to sign in with it." />
          )}
          {!ids.length && githubMember && mine.canLink.length > 0 && <p className="border-t border-border-subtle px-4 py-3 text-sm text-fg-subtle">No Google or Microsoft account is linked yet.</p>}
          {mine.canLink.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle px-4 py-3">
              {mine.canLink.map((p) =>
                p.id === 'github' ? null : (
                  <Button key={p.id} size="sm" variant="secondary" icon={<ProviderMark id={p.id} />} loading={linking === p.id} disabled={!!linking} onClick={() => void link(p.id as OidcProviderId)}>
                    Link {p.label}
                  </Button>
                ),
              )}
              <span className="text-sm text-fg-subtle">You sign in with the account once to prove it is yours.</span>
            </div>
          )}
          {mine.canLink.length === 0 && !viaToken && (
            <p className="border-t border-border-subtle px-4 py-3 text-sm text-fg-subtle">Google and Microsoft sign-in are off on this hub, so there is nothing to link yet.</p>
          )}
          {member && !ownerLink && !viaToken && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle px-4 py-3">
              <Button size="sm" variant="ghost" icon={<LogOut />} onClick={() => setEndingAll(true)}>
                Sign out everywhere
              </Button>
              <span className="text-sm text-fg-subtle">Signs out every browser signed in as you, this one included.</span>
            </div>
          )}
        </>
      )}
      <ConfirmDialog
        open={endingAll}
        onOpenChange={setEndingAll}
        title="Sign out everywhere?"
        description="Unlink any sign-in you do not recognise first, or it can sign you in again."
        confirmLabel="Sign out everywhere"
        onConfirm={async () => {
          const r = await authClient.signOutEverywhere();
          toast.success('You are signed out everywhere', `${r.sessionsEnded} ${r.sessionsEnded === 1 ? 'session was' : 'sessions were'} ended. Sign in again to continue.`);
          setEndingAll(false);
          onChanged();
        }}
      />
      <ConfirmDialog
        open={!!unlinking}
        onOpenChange={(v) => !v && setUnlinking(null)}
        title={unlinking ? `Unlink ${unlinking.email ?? SIGN_IN_PROVIDER_LABEL[unlinking.provider]}?` : ''}
        description="This account can no longer sign in, and its sessions are signed out."
        confirmLabel="Unlink"
        onConfirm={async () => {
          if (!unlinking) return;
          const r = await authClient.unlinkIdentity(unlinking.id);
          toast.success('The account was unlinked', r.sessionsEnded ? `${r.sessionsEnded} ${r.sessionsEnded === 1 ? 'session was' : 'sessions were'} signed out.` : undefined);
          setUnlinking(null);
          onChanged();
        }}
      />
    </Panel>
  );
}

// ------------------------------------------------------------------ providers

function snippetFor(id: ProviderStatus['id']): string {
  if (id === 'google') return JSON.stringify({ google: { clientId: '1234-abc.apps.googleusercontent.com', clientSecret: 'GOCSPX-...' } }, null, 2);
  if (id === 'microsoft') return JSON.stringify({ microsoft: { clientId: '00000000-0000-0000-0000-000000000000', clientSecret: '...', tenant: 'organizations' } }, null, 2);
  return JSON.stringify({ github: { clientId: 'Ov23li...', clientSecret: '...' }, allow: { users: { 'your-github-login': 'owner' } } }, null, 2);
}

const ENV_HINT: Record<ProviderStatus['id'], string> = {
  github: 'ORGUS_GITHUB_CLIENT_ID and ORGUS_GITHUB_CLIENT_SECRET',
  google: 'ORGUS_GOOGLE_CLIENT_ID and ORGUS_GOOGLE_CLIENT_SECRET',
  microsoft: 'ORGUS_MICROSOFT_CLIENT_ID, ORGUS_MICROSOFT_CLIENT_SECRET and ORGUS_MICROSOFT_TENANT',
};

function SetupSteps({ p, configFile }: { p: ProviderStatus; configFile: string }) {
  const file = configFile || 'auth.json';
  return (
    <Steps>
      {p.id === 'google' && (
        <Step n={1}>
          <p>
            In the Google Cloud console, open Google Auth Platform, Clients:{' '}
            <ExtLink href="https://console.cloud.google.com/auth/clients">console.cloud.google.com/auth/clients</ExtLink>. Set up branding and the audience (Internal for one Workspace, External for anyone), then
            create a Web application client.
          </p>
        </Step>
      )}
      {p.id === 'microsoft' && (
        <Step n={1}>
          <p>
            In the Microsoft Entra admin center, open App registrations, New registration:{' '}
            <ExtLink href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade">entra.microsoft.com</ExtLink>. Pick the supported account types that match the tenant setting.
          </p>
        </Step>
      )}
      {p.id === 'github' && (
        <Step n={1}>
          <p>
            Register an OAuth App on GitHub: <ExtLink href="https://github.com/settings/applications/new">github.com/settings/applications/new</ExtLink>. Tick Enable Device Flow so orgus login works.
          </p>
        </Step>
      )}
      <Step n={2}>
        <p>{p.id === 'github' ? 'Use this authorization callback URL, exactly:' : `Add this ${p.id === 'microsoft' ? 'Web ' : ''}redirect URI, exactly. The port counts.`}</p>
        {p.redirectUri ? <CopyField value={p.redirectUri} label="Redirect URI" /> : <p className="text-sm text-fg-subtle">Only admins see the redirect URI.</p>}
      </Step>
      {p.id === 'microsoft' && (
        <Step n={3}>
          <p>
            Add a client secret (note its expiry) under Certificates and secrets, and the optional ID token claims email, xms_edov and acct under Token configuration.
          </p>
        </Step>
      )}
      <Step n={p.id === 'microsoft' ? 4 : 3}>
        <p>
          Put the client id and secret in <span className="break-all font-mono text-sm">{file}</span>, or set {ENV_HINT[p.id]} on the hub.
        </p>
        <CopyField value={snippetFor(p.id)} label={`${p.label} settings for auth.json`} multiline />
      </Step>
      <Step n={p.id === 'microsoft' ? 5 : 4}>
        <p>Reload this page. The hub reads the file again by itself; no restart needed. The owner link keeps working either way.</p>
      </Step>
    </Steps>
  );
}

/** A fact: the label above the value on a phone, beside it from 768 px, so a URL gets the card's width. */
const FACT_ROW = 'flex flex-col gap-1 md:flex-row md:items-start md:gap-4';
const FACT_LABEL = 'shrink-0 text-caption text-fg-subtle md:w-32 md:text-sm';

const PROVIDER_BLURB: Record<ProviderStatus['id'], string> = {
  github: 'People on the GitHub allow list in Members sign in with their GitHub account.',
  google: 'Personal Gmail and Google Workspace accounts. They need an invite, a linked account or a join rule.',
  microsoft: 'Microsoft work, school and personal accounts. They need an invite, a linked account or a join rule.',
};

export function ProviderCard({ p, admin, configFile }: { p: ProviderStatus; admin: boolean; configFile: string }) {
  const [showSteps, setShowSteps] = useState(false);
  const st = providerStatusOf(p);
  const ms = p.microsoft;
  return (
    <Panel
      title={`Sign in with ${p.label}`}
      icon={<ProviderMark id={p.id} />}
      actions={
        <span className="flex items-center gap-2 pr-2">
          {p.testIssuer && st.label !== 'Test identity provider' && <StatusPill status="needs-you" label="Test identity provider" />}
          <StatusPill status={st.status} label={st.label} />
        </span>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-base text-fg-muted">{PROVIDER_BLURB[p.id]}</p>
        {admin && p.problems.length > 0 && (
          <ul
            className={
              p.needsAttention
                ? 'flex flex-col gap-2 rounded-control border border-status-danger-line bg-status-danger-soft px-3 py-2'
                : 'flex flex-col gap-2 rounded-control border border-status-attention-line bg-status-attention-soft px-3 py-2'
            }
            aria-label={p.needsAttention ? `${p.label} problems` : `${p.label} notes`}
          >
            {p.problems.map((x) => (
              <li key={x} className="flex gap-2 text-sm text-fg">
                <CircleAlert className={p.needsAttention ? 'mt-1 size-3.5 shrink-0 text-status-danger' : 'mt-1 size-3.5 shrink-0 text-status-attention'} aria-hidden />
                <span className="min-w-0 break-words">{x}</span>
              </li>
            ))}
            {p.needsAttention && <li className="pl-6 text-sm text-fg-muted">Fix it in {configFile || 'auth.json'}, then reload this page.</li>}
          </ul>
        )}
        {admin && p.configured && (
          <dl className="flex flex-col gap-2 text-sm">
            <div className={FACT_ROW}>
              <dt className={cn(FACT_LABEL, 'md:pt-2')}>Redirect URI</dt>
              <dd className="min-w-0 flex-1">
                <CopyField value={p.redirectUri} label={`${p.label} redirect URI`} />
              </dd>
            </div>
            {p.clientId && (
              <div className={FACT_ROW}>
                <dt className={FACT_LABEL}>Client id</dt>
                <dd className="min-w-0 flex-1 break-all font-mono text-fg">{p.clientId}</dd>
              </div>
            )}
            {ms && (
              <div className={FACT_ROW}>
                <dt className={FACT_LABEL}>Accounts</dt>
                <dd className="min-w-0 flex-1 text-fg">
                  {MICROSOFT_AUDIENCE_LABEL[ms.audience]} <span className="font-mono text-fg-subtle">(tenant {ms.tenant})</span>
                </dd>
              </div>
            )}
            {ms && ms.pinnedTenantId && ms.pinnedTenantId !== ms.tenant && (
              <div className={FACT_ROW}>
                <dt className={FACT_LABEL}>Tenant id</dt>
                <dd className="min-w-0 flex-1 break-all font-mono text-fg">{ms.pinnedTenantId}</dd>
              </div>
            )}
          </dl>
        )}
        {admin && (!p.configured || showSteps) && <SetupSteps p={p} configFile={configFile} />}
        {admin && p.configured && (
          <div>
            <Button size="sm" variant="ghost" onClick={() => setShowSteps((v) => !v)} aria-expanded={showSteps}>
              {showSteps ? 'Hide setup steps' : 'Show setup steps'}
            </Button>
          </div>
        )}
        {!admin && !p.configured && <p className="text-sm text-fg-subtle">Off until an admin turns it on.</p>}
      </div>
    </Panel>
  );
}

// ------------------------------------------------------------------ who may use Google and Microsoft

export function GooglePolicy({ p, canConfigure, onChanged }: { p: ProviderStatus; canConfigure: boolean; onChanged: () => void }) {
  const { save, dialog } = usePolicySave(onChanged);
  const domains = p.google?.allowedDomains ?? [];
  const mine = settingsValues(domains);
  const set = (next: string[], what: string) => save({ google: { allowedDomains: next } }, what).catch((e) => toast.error(e));
  return (
    <Panel title="Who may use Google" icon={<Globe />}>
      <div className="flex flex-col gap-3">
        <p className="flex items-center gap-2 text-base text-fg">
          <StatusPill status={domains.length ? 'done' : 'idle'} variant="plain" label={domains.length ? 'Only these Workspace domains' : 'Any Google account may try'} />
        </p>
        <p className="text-sm text-fg-muted">
          {domains.length
            ? 'Accounts outside these Google Workspace domains are refused, including personal Gmail. Members who signed in with one are signed out when its domain is removed.'
            : 'Personal Gmail and any Workspace account may try to sign in. They still need an invite, a linked account or a join rule. Add a domain to accept only that Workspace.'}
        </p>
        {domains.length > 0 && (
          <div className="flex flex-wrap gap-2" aria-label="Allowed domains for Google Workspace">
            {domains.map((d) => (
              <ValueChip key={`${d.source}:${d.value}`} value={d.value} source={d.source} canEdit={canConfigure} onRemove={() => void set(mine.filter((x) => x !== d.value), `${d.value} was removed`)} />
            ))}
          </div>
        )}
        {canConfigure ? (
          <AddValueForm
            label="Workspace domain"
            placeholder="Workspace domain"
            check={(v) => checkWorkspaceDomain(v) ?? (domains.some((d) => d.value === v) ? `${v} is already on the list.` : null)}
            onAdd={(v) => save({ google: { allowedDomains: [...mine, v] } }, `Only listed Workspace domains may use Google now`)}
          />
        ) : (
          <OwnerOnlyNote />
        )}
      </div>
      {dialog}
    </Panel>
  );
}

function TrustedDomainsForm({ tenants, onAdd }: { tenants: string[]; onAdd: (tid: string, domain: string) => Promise<void> }) {
  const [tid, setTid] = useState(tenants[0] ?? '');
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const t = norm(tid);
    const d = norm(domain);
    const problem = checkTenant(t, false) ?? (d ? checkTrustedDomain(d) : 'Type a domain first.');
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      await onAdd(t, d);
      setDomain('');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2" noValidate>
      <div className="flex flex-wrap items-start gap-2">
        {tenants.length > 0 ? (
          <Select size="sm" aria-label="Tenant" value={tid} onValueChange={setTid} options={tenants.map((t) => ({ value: t, label: `Tenant ${shortTenant(t)}` }))} />
        ) : (
          <Input size="sm" aria-label="Tenant id" placeholder="Tenant id" value={tid} onChange={(e) => setTid(e.target.value)} wrapperClassName="w-72 max-w-full" className="font-mono" autoComplete="off" spellCheck={false} />
        )}
        <Input size="sm" aria-label="Email domain" placeholder="Email domain" value={domain} onChange={(e) => setDomain(e.target.value)} wrapperClassName="min-w-48 flex-1" autoComplete="off" spellCheck={false} />
        <Button type="submit" size="sm" variant="secondary" icon={<Plus />} loading={busy}>
          Trust
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-failed">
          {error}
        </p>
      )}
    </form>
  );
}

export function MicrosoftPolicy({ p, canConfigure, onChanged }: { p: ProviderStatus; canConfigure: boolean; onChanged: () => void }) {
  const { save, dialog } = usePolicySave(onChanged);
  const ms = p.microsoft;
  if (!ms) return null;
  const single = ms.audience === 'single';
  const tenants = ms.allowedTenants;
  const myTenants = settingsValues(tenants);
  const trusted = ms.trustedEmailDomains;
  const myTrusted = trustedFromSettings(p);
  const setTenants = (next: string[], what: string) => save({ microsoft: { allowedTenants: next } }, what);
  const setTrusted = (next: Record<string, string[]>, what: string) => save({ microsoft: { trustedEmailDomains: next } }, what);
  const tenantName = (t: string) => (t === MICROSOFT_CONSUMERS_TENANT ? 'Personal accounts' : t);
  return (
    <Panel title="Who may use Microsoft" icon={<Building2 />}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-fg-subtle">Accounts the app accepts (set in auth.json)</span>
          <span className="text-base text-fg">
            {MICROSOFT_AUDIENCE_LABEL[ms.audience]}
            {single && <span className="font-mono text-sm text-fg-subtle"> {ms.pinnedTenantId ?? ms.tenant}</span>}
          </span>
        </div>

        {!single && (
          <section className="flex flex-col gap-2" aria-labelledby="ms-tenants">
            <h4 id="ms-tenants" className="text-md font-medium text-fg">
              Organisations that may try
            </h4>
            <p className="flex items-center gap-2">
              <StatusPill status={tenants.length ? 'done' : 'idle'} variant="plain" label={tenants.length ? 'Only these tenants' : 'Any tenant the app accepts'} />
            </p>
            <p className="text-sm text-fg-muted">
              Limits who may try, never whose email counts as proven; add {MICROSOFT_CONSUMERS_TENANT} for personal accounts.
            </p>
            {tenants.length > 0 && (
              <div className="flex flex-wrap gap-2" aria-label="Allowed tenants for Microsoft">
                {tenants.map((t) => (
                  <ValueChip
                    key={`${t.source}:${t.value}`}
                    value={tenantName(t.value)}
                    title={t.value}
                    source={t.source}
                    canEdit={canConfigure}
                    onRemove={() => void setTenants(myTenants.filter((x) => x !== t.value), 'The tenant was removed').catch((e) => toast.error(e))}
                  />
                ))}
              </div>
            )}
            {canConfigure && (
              <AddValueForm
                label="Tenant id"
                placeholder="Tenant id"
                check={(v) => checkTenant(v, true) ?? (tenants.some((t) => t.value === v) ? 'That tenant is already on the list.' : null)}
                onAdd={(v) => setTenants([...myTenants, v], 'The tenant may sign in now')}
              />
            )}
          </section>
        )}

        <section className="flex flex-col gap-2" aria-labelledby="ms-trusted">
          <h4 id="ms-trusted" className="text-md font-medium text-fg">
            Trusted email domains
          </h4>
          <p className="text-sm text-fg-muted">
            Emails count as proven only for these domains when Microsoft marks them verified (xms_edov); others join by invite.
          </p>
          {trusted.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-fg-subtle">
              <StatusPill status="idle" variant="plain" label="None" /> No Microsoft email counts as proven.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" aria-label="Trusted email domains">
              {trusted.flatMap((t) =>
                t.domains.map((d) => (
                  <ValueChip
                    key={`${t.source}:${t.tenantId}:${d}`}
                    value={d}
                    detail={`tenant ${shortTenant(t.tenantId)}`}
                    title={`${d}, tenant ${t.tenantId}`}
                    source={t.source}
                    canEdit={canConfigure}
                    onRemove={() => {
                      const left = (myTrusted[t.tenantId] ?? []).filter((x) => x !== d);
                      const next = { ...myTrusted };
                      if (left.length) next[t.tenantId] = left;
                      else delete next[t.tenantId];
                      void setTrusted(next, `${d} is no longer trusted`).catch((e) => toast.error(e));
                    }}
                  />
                )),
              )}
            </div>
          )}
          {canConfigure ? (
            <TrustedDomainsForm
              tenants={[ms.pinnedTenantId ?? '', ...myTenants].filter((t) => t && t !== MICROSOFT_CONSUMERS_TENANT)}
              onAdd={(tid, d) => setTrusted({ ...myTrusted, [tid]: [...new Set([...(myTrusted[tid] ?? []), d])] }, `${d} is trusted now`)}
            />
          ) : (
            <OwnerOnlyNote />
          )}
        </section>
      </div>
      {dialog}
    </Panel>
  );
}

// ------------------------------------------------------------------ join rules

function AddJoinRuleForm({ google, microsoft, onAdded }: { google: boolean; microsoft: boolean; onAdded: () => void }) {
  const [provider, setProvider] = useState<OidcProviderId>(google ? 'google' : 'microsoft');
  const [value, setValue] = useState('');
  const [role, setRole] = useState<'viewer' | 'operator'>('viewer');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = norm(value);
    const problem = !v ? 'Type a domain or tenant id first.' : provider === 'google' ? checkWorkspaceDomain(v) : checkTenant(v, false);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      await authClient.addJoinRule({ provider, kind: provider === 'google' ? 'domain' : 'tenant', value: v, role });
      toast.success('Join rule added', `New people from ${v} join as ${ROLE_LABEL[role].toLowerCase()} the first time they sign in.`);
      setValue('');
      onAdded();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-start gap-2 border-b border-border-subtle p-3" noValidate>
      <Segmented
        label="Provider"
        value={provider}
        onChange={(v) => {
          setProvider(v);
          setError(null);
        }}
        options={[
          { value: 'google', label: 'Google domain', icon: <ProviderMark id="google" />, disabled: !google },
          { value: 'microsoft', label: 'Microsoft tenant', icon: <ProviderMark id="microsoft" />, disabled: !microsoft },
        ]}
      />
      <Input
        size="sm"
        aria-label={provider === 'google' ? 'Workspace domain' : 'Tenant id'}
        placeholder={provider === 'google' ? 'Workspace domain' : 'Tenant id'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={error ?? undefined}
        wrapperClassName="min-w-56 flex-1"
        autoComplete="off"
        spellCheck={false}
      />
      <Select
        size="sm"
        aria-label="Role"
        value={role}
        onValueChange={(v) => setRole(v as 'viewer' | 'operator')}
        options={[
          { value: 'viewer', label: ROLE_LABEL.viewer },
          { value: 'operator', label: ROLE_LABEL.operator },
        ]}
      />
      <Button type="submit" size="sm" variant="primary" icon={<Plus />} loading={busy}>
        Add rule
      </Button>
    </form>
  );
}

export function JoinRulesPanel({ rules, canConfigure, google, microsoft, onChanged }: { rules: JoinRule[]; canConfigure: boolean; google: boolean; microsoft: boolean; onChanged: () => void }) {
  const [removing, setRemoving] = useState<JoinRule | null>(null);
  const columns: Column<JoinRule>[] = [
    {
      key: 'who',
      header: 'Who joins',
      grow: true,
      text: (r) => r.value,
      sortValue: (r) => r.value,
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <ProviderMark id={r.provider} className="size-4 shrink-0 text-fg-subtle" />
          <span className="min-w-0">
            <span className="block truncate font-mono text-sm text-fg" title={r.value}>
              {r.value}
            </span>
            <span className="block text-sm text-fg-subtle">{r.kind === 'domain' ? 'Google Workspace domain, proven email only' : 'Microsoft tenant, members only, never guests'}</span>
          </span>
        </span>
      ),
    },
    { key: 'role', header: 'Role', type: 'badge', sortValue: (r) => ROLE_RANK[r.role], cell: (r) => <span className={ROLE_VALUE_CLASS}>{ROLE_LABEL[r.role]}</span> },
    {
      key: 'source',
      header: 'Added',
      hideBelow: 'md',
      cell: (r) =>
        r.source === 'config' ? (
          <SourceTag source="config" />
        ) : (
          <span className="text-sm text-fg-subtle">
            {r.addedBy ?? 'Settings'}, <RelativeTime ts={r.addedAt} />
          </span>
        ),
    },
  ];
  return (
    <Panel title="Join automatically" sub={`${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`} icon={<UserPlus />} flush>
      {canConfigure && (google || microsoft) && <AddJoinRuleForm google={google} microsoft={microsoft} onAdded={onChanged} />}
      <DataTable
        frameless
        label="Join rules"
        rows={rules}
        rowKey={(r) => r.id}
        columns={columns}
        pageSize={false}
        empty={<EmptyState icon={<UserPlus />} title="Nobody joins on their own" description="Add a rule to let a Workspace domain or Microsoft tenant join without an invite." />}
        rowActions={(r) => (canConfigure && r.source === 'settings' ? <IconButton label={`Remove the join rule for ${r.value}`} icon={<Trash2 />} variant="ghost" size="sm" onClick={() => setRemoving(r)} /> : null)}
      />
      {!canConfigure && (
        <div className="border-t border-border-subtle px-4 py-3">
          <OwnerOnlyNote />
        </div>
      )}
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? `Remove the join rule for ${removing.value}?` : ''}
        description="New people need an invite again; members who joined stay until removed in Members."
        confirmLabel="Remove rule"
        onConfirm={async () => {
          if (!removing) return;
          await authClient.removeJoinRule(removing.id);
          toast.success('Join rule removed');
          setRemoving(null);
          onChanged();
        }}
      />
    </Panel>
  );
}

// ------------------------------------------------------------------ the section

/** The settings body once the data is in; split out so tests can render it with fixed data. */
export function SignInSettingsBody({
  me,
  settings,
  mine,
  mineError,
  onRetryMine,
  onChanged,
}: {
  me: Me;
  settings: SignInSettings;
  mine: MyIdentities | null;
  mineError: unknown;
  onRetryMine: () => void;
  onChanged: () => void;
}) {
  const admin = me.actor.kind === 'human' && roleAtLeast(me.actor.role, 'admin');
  const byId = new Map(settings.providers.map((p) => [p.id, p] as const));
  const google = byId.get('google');
  const microsoft = byId.get('microsoft');
  const anyOn = settings.providers.some((p) => p.configured);
  const oidcOn = !!google?.configured || !!microsoft?.configured;
  return (
    <div className="flex flex-col gap-4">
      {!anyOn && (
        <div role="status" className="flex items-start gap-3 rounded-card border border-border bg-raised px-4 py-3">
          <StatusPill status="idle" label="Owner link only" />
          <p className="text-base text-fg-muted">
            Only the owner link signs people in here. {admin ? 'Turn on GitHub, Google or Microsoft below.' : 'An admin can turn on GitHub, Google or Microsoft sign-in.'}
          </p>
        </div>
      )}
      <YourSignIns me={me} mine={mine} error={mineError} onRetry={onRetryMine} onChanged={onChanged} />
      <section aria-label="Sign-in providers" className="flex flex-col gap-4">
        {settings.providers.map((p) => (
          <ProviderCard key={p.id} p={p} admin={admin} configFile={settings.configFile} />
        ))}
      </section>
      {admin && google?.configured && <GooglePolicy p={google} canConfigure={settings.canConfigure} onChanged={onChanged} />}
      {admin && microsoft?.configured && <MicrosoftPolicy p={microsoft} canConfigure={settings.canConfigure} onChanged={onChanged} />}
      {admin && oidcOn && <JoinRulesPanel rules={settings.joinRules} canConfigure={settings.canConfigure} google={!!google?.configured} microsoft={!!microsoft?.configured} onChanged={onChanged} />}
    </div>
  );
}

export function SignInSettingsView() {
  const me = useMe((s) => s.me);
  // Re-read when the hub says auth changed (state.ext.auth.rev over SSE), instead of polling.
  const rev = useExt<AuthState>('auth')?.rev;
  const settings = useResource(authClient.signInSettings, undefined, rev);
  const mine = useResource(authClient.myIdentities, undefined, rev);
  const refresh = () => {
    void settings.refresh();
    void mine.refresh();
  };

  if ((settings.loading && !settings.data) || !me) {
    return (
      <div className="flex flex-col gap-4" role="status" aria-label="Loading sign-in settings">
        <SkeletonRows rows={2} />
        <SkeletonRows rows={3} />
        <SkeletonRows rows={3} />
      </div>
    );
  }
  if (!settings.data) return <ErrorState error={settings.error} onRetry={settings.refresh} />;
  return (
    <div className="flex flex-col gap-4">
      {settings.error != null && <ErrorState variant="inline" error={settings.error} onRetry={settings.refresh} />}
      <SignInSettingsBody me={me} settings={settings.data} mine={mine.data} mineError={mine.error} onRetryMine={() => void mine.refresh()} onChanged={refresh} />
    </div>
  );
}
